import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/utils/logger";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { TosConsentGate } from "@/components/auth/TosConsentGate";

interface AdminProtectedRouteProps {
  children: ReactNode;
}

export default function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const location = useLocation();
  const { t } = useLanguage();
  const { currentOrganization, isReady } = useOrganizationContext();
  const { isPlatformAdmin } = usePermissions();
  const error = null;

  // Wait for organization context to be fully ready before checking permissions
  const isLoading = !isReady;

  // Log access attempt
  logger.debug(
    "auth",
    "admin-route-access-attempt",
    `Admin route access attempt at ${location.pathname}`,
    { path: location.pathname, orgId: currentOrganization?.id, isReady, isLoading }
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    logger.error("auth", "admin-route-error", "Error checking admin access", error);
    toast.error(t.auth.authenticationError);
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  }

  if (!isPlatformAdmin) {
    logger.warn(
      "auth",
      "unauthorized-admin-access",
      "Non-admin user attempted to access admin route",
      { path: location.pathname, orgId: currentOrganization?.id }
    );
    toast.error(t.auth.adminPrivilegesRequired);
    return <Navigate to="/" replace />;
  }

  // Log successful admin route access
  logger.info(
    "auth",
    "admin-route-accessed",
    `Admin successfully accessed route ${location.pathname} in organization ${currentOrganization?.id || "N/A"}`
  );

  return <TosConsentGate>{children}</TosConsentGate>;
}
